import type { RecordingState, Task } from "@xinghai/shared";
import { stripTone as splitTone, toToneMarks, toToneMarksSequence } from "@xinghai/shared";
import type { IconId, Tone } from "@xinghai/ui";
import type { RecordingListItem } from "../../lib/api";

/** Student-facing task names — no internal codes in the copy. */
export const TASK_LABEL: Record<Task, string> = {
  T1_reference: "示范朗读",
  T1b_reference_singing: "示范演唱",
  T2_diagnostic: "单字",
  T3_tongue_twister: "绕口令",
  T4_passage: "段落",
  T5_lyric_reading: "歌词朗读",
  T6_singing: "演唱",
  T7_perception: "听辨",
};

/** Icon per task (status and type are always icon + text, never colour alone). */
export const TASK_ICONS: Record<Task, IconId> = {
  T1_reference: "ui-reading",
  T1b_reference_singing: "ui-speaking",
  T2_diagnostic: "ui-hash",
  T3_tongue_twister: "ui-speaking",
  T4_passage: "ui-document",
  T5_lyric_reading: "ui-reading",
  T6_singing: "ui-speaking",
  T7_perception: "ui-listening",
};

/** One-line coaching hint shown above the material when the server has none. */
export const TASK_HINT: Partial<Record<Task, string>> = {
  T2_diagnostic: "看清拼音，按平时说话的音量读一遍。",
  T3_tongue_twister: "先慢速读清楚，再用正常语速读一遍。",
  T4_passage: "一句一句来，读错了可以重录当前这句。",
  T5_lyric_reading: "先把这句歌词读清楚，再进入演唱。",
  T6_singing: "按你熟悉的旋律唱这句，咬字清楚就好。",
  T7_perception: "听清楚再选，凭第一感觉就行。",
};

/** Rough (fractional) minutes per take when the server does not send an estimate — spec §2.2. */
const DEFAULT_ETA: Record<Task, number> = {
  T1_reference: 0.5,
  T1b_reference_singing: 1,
  T2_diagnostic: 0.03,
  T3_tongue_twister: 0.5,
  T4_passage: 1.5,
  T5_lyric_reading: 0.15,
  T6_singing: 0.3,
  T7_perception: 0.05,
};

/** Whole minutes for display: never 0, always rounded up. */
export function wholeMinutes(m: number): number {
  return Math.max(1, Math.ceil(m - 1e-9));
}

export function etaMinutes(task: Task, given?: number | null): number {
  return given && given > 0 ? given : DEFAULT_ETA[task];
}

/**
 * How many uploads an assignment needs. The server sends `required_count`
 * (T3 = 2 passes, T4 = one per sentence); this is only the last-resort
 * fallback for a payload that omits it.
 */
export function requiredTakes(requiredCount?: number | null): number {
  return requiredCount && requiredCount > 0 ? requiredCount : 1;
}

/**
 * Students never see a numbered syllable like `si4`. Locked pinyin is stored
 * in whatever form the material import used; this renders it with the tone
 * mark (`sì`) via the shared phone tables.
 */
export function displayPinyin(pinyin: string): string {
  if (!pinyin) return "";
  return pinyin.includes(" ") ? toToneMarksSequence(pinyin) : toToneMarks(pinyin);
}

/**
 * 红线 14.1 in the UI: the singing screen must not prime a Mandarin tone, so
 * the syllable is rendered with the tone neutralised (digit forced to 5),
 * which drops both the mark and the number.
 */
export function stripTone(pinyin: string): string {
  if (!pinyin) return "";
  const [base] = splitTone(pinyin);
  if (!base) return pinyin;
  return toToneMarks(`${base}5`);
}

export interface StatusPresentation {
  label: string;
  tone: Tone;
  icon: IconId;
  /** QC note shown under the row when the take was rejected. */
  note?: string | null;
}

/** docs/UX.md 4 — 待质检 / 已通过 / 需重录 (+ reason). */
export function recordingStatus(rec: Pick<RecordingListItem, "state" | "qc">): StatusPresentation {
  const state: RecordingState = rec.state;
  if (state === "qc_reject") {
    return {
      label: "需重录",
      tone: "danger",
      icon: "ui-refresh",
      note: rec.qc?.note ?? "老师建议你重新录一遍。",
    };
  }
  if (state === "uploaded") {
    return { label: "待质检", tone: "warning", icon: "ui-clock" };
  }
  return { label: "已通过", tone: "success", icon: "ui-check-circle" };
}

/** `2 分 03 秒`-free, compact duration for lists. */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return "—";
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s} 秒`;
}
